import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Card from "../components/Card";
import { mlmAPI, userAPI } from "../services/api";

const TeamMemberDetails = () => {
  const navigate = useNavigate();
  const { memberId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [member, setMember] = useState(null);
  const [memberLevel, setMemberLevel] = useState("N/A");
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    if (!memberId) {
      setError("Invalid team member ID.");
      setLoading(false);
      return;
    }

    const fetchMemberDetails = async () => {
      try {
        setLoading(true);
        setError("");

        const [userRes, levelWiseRes, summaryRes] = await Promise.allSettled([
          userAPI.getUserById(memberId),
          mlmAPI.getLevelWiseReferrals(20),
          mlmAPI.getDownlineMemberSummary(memberId),
        ]);

        if (userRes.status !== "fulfilled") {
          throw new Error("Team member not found.");
        }

        setMember(userRes.value || null);

        if (summaryRes.status === "fulfilled") {
          setSummary(summaryRes.value);
        } else {
          setSummary(null);
        }

        if (levelWiseRes.status === "fulfilled") {
          const levelWise = levelWiseRes.value || {};
          let foundLevel = "N/A";
          Object.entries(levelWise).forEach(([levelKey, users]) => {
            if (Array.isArray(users) && users.some((u) => u.id === memberId)) {
              const numericLevel = String(levelKey).replace("level", "");
              foundLevel = numericLevel || "N/A";
            }
          });
          setMemberLevel(foundLevel);
        } else {
          setMemberLevel("N/A");
        }
      } catch (e) {
        setError(e?.message || "Failed to load team member details.");
      } finally {
        setLoading(false);
      }
    };

    fetchMemberDetails();
  }, [memberId]);

  const basicDetails = useMemo(() => {
    if (!member) return [];
    return [
      { label: "User ID", value: member.id || "-" },
      { label: "Name", value: member.name || "-" },
      { label: "Email", value: member.email || "-" },
      { label: "Phone", value: member.phone || "-" },
      { label: "Role", value: member.role || "-" },
      { label: "Status", value: member.isActive ? "Active" : "Inactive" },
      {
        label: "Join Date",
        value: member.createdAt
          ? new Date(member.createdAt).toLocaleString()
          : "-",
      },
      { label: "Team Level", value: memberLevel === "N/A" ? "N/A" : `L${memberLevel}` },
    ];
  }, [member, memberLevel]);

  return (
    <div className="flex min-h-screen bg-[var(--bg-main)]">
      <Sidebar />
      <div className="flex-1 p-4 pt-16 md:p-8 md:pt-8 md:ml-64">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2 bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
            Team Member Details
          </h1>
          <p className="text-[var(--text-primary)] text-sm md:text-base">
            Basic team member information
          </p>
        </div>

        <Card>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
              Basic Details
            </h2>
            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 rounded-lg border border-primary/40 text-primary text-sm font-semibold hover:bg-[#22c55e]/10 transition-all"
            >
              Back
            </button>
          </div>

          {loading ? (
            <div className="py-10 text-center text-[var(--text-primary)] opacity-70">
              Loading team member details...
            </div>
          ) : error ? (
            <div className="py-10 text-center text-red-400 font-semibold">
              {error}
            </div>
          ) : !member ? (
            <div className="py-10 text-center text-[var(--text-primary)] opacity-70">
              Team member details not found.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {basicDetails.map((item) => (
                <div
                  key={item.label}
                  className="p-4 rounded-xl bg-[var(--bg-card)] border border-primary/10"
                >
                  <p className="text-xs text-[var(--text-primary)] opacity-70 mb-1">{item.label}</p>
                  <p className="text-sm md:text-base text-[var(--text-primary)] break-all">
                    {item.value}
                  </p>
                </div>
              ))}
            </div>
          )}
        </Card>

        {summary?.wallet ? (
          <Card className="mt-6">
            <h2 className="text-xl font-bold mb-4 text-[var(--text-primary)]">Wallet snapshot</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm text-[var(--text-primary)]">
              <div>
                <p className="opacity-60">Main</p>
                <p className="font-semibold">{Number(summary.wallet.mainBalance).toFixed(2)}</p>
              </div>
              <div>
                <p className="opacity-60">Profit</p>
                <p className="font-semibold">{Number(summary.wallet.profitBalance).toFixed(2)}</p>
              </div>
              <div>
                <p className="opacity-60">Reward</p>
                <p className="font-semibold">{Number(summary.wallet.rewardBalance).toFixed(2)}</p>
              </div>
              <div>
                <p className="opacity-60">Rewards (earnings)</p>
                <p className="font-semibold">
                  {Number(summary.totalGeneratedRewards || 0).toFixed(2)}
                </p>
              </div>
            </div>
          </Card>
        ) : null}

        {summary?.recentTransactions?.length ? (
          <Card className="mt-6">
            <h2 className="text-xl font-bold mb-4 text-[var(--text-primary)]">Recent transactions</h2>
            <div className="space-y-2 max-h-64 overflow-y-auto text-sm">
              {summary.recentTransactions.slice(0, 15).map((tx) => (
                <div
                  key={tx.id}
                  className="flex justify-between border-b border-primary/10 py-2 text-[var(--text-primary)]"
                >
                  <span>{tx.type}</span>
                  <span>{Number(tx.amount).toFixed(4)}</span>
                </div>
              ))}
            </div>
          </Card>
        ) : null}
      </div>
    </div>
  );
};

export default TeamMemberDetails;
